<template>
  <div class="hero-split">
    <!-- Left rail: device list -->
    <div class="rail">
      <div class="rail-header">
        <span class="rail-title">Devices</span>
        <a-tag color="blue">{{ devices.length }}</a-tag>
      </div>
      <a-input-search
        v-model:value="searchTerm"
        placeholder="Search"
        size="small"
        allow-clear
        class="rail-search"
      />
      <div class="rail-list">
        <div
          v-for="row in filteredRows"
          :key="row.serial"
          class="rail-item"
          :class="{ 'rail-item--selected': row.serial === selectedSerial }"
          @click="selectedSerial = row.serial"
        >
          <span class="rail-dot" :style="{ background: modeDotColor(row.mode) }" />
          <div class="rail-item-body">
            <div class="rail-item-name">
              {{ row.deviceName }}
              <star-filled v-if="row.starred" class="rail-star" />
            </div>
            <div class="rail-item-sub">
              <template v-if="row.mode === 'guidance'">
                {{ formatNumber(row.cyclesToday) }} cycles
              </template>
              <template v-else-if="row.mode === 'recording'">
                Recording{{
                  row.hoursRecorded ? ` · ${row.hoursRecorded}` : ''
                }}
              </template>
              <template v-else-if="row.mode === 'idle'">Idle</template>
              <template v-else>Offline</template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right pane: hero card -->
    <div class="hero">
      <template v-if="selectedRow">
        <div class="hero-header">
          <div>
            <div class="hero-title">
              {{ selectedRow.deviceName }}
              <star-filled v-if="selectedRow.starred" class="hero-star" />
              <a-tag
                :color="modeTagColor(selectedRow.mode)"
                class="ml-2"
              >
                <span
                  class="dot"
                  :style="{ background: modeDotColor(selectedRow.mode) }"
                />
                {{ modeLabel(selectedRow.mode) }}
              </a-tag>
            </div>
            <div v-if="selectedRow.taskName" class="hero-subtitle">
              {{ selectedRow.taskName }}
            </div>
          </div>
          <a-space>
            <a-button
              type="primary"
              ghost
              :disabled="!selectedRow.isActive"
              @click="handleOpenAnalytics(selectedRow)"
            >
              <template #icon><line-chart-outlined /></template>
              Analytics
            </a-button>
            <a-button
              :disabled="!selectedRow.isActive"
              @click="handleOpenTrace(selectedRow)"
            >
              <template #icon><play-circle-outlined /></template>
              Trace
            </a-button>
          </a-space>
        </div>

        <div class="hero-preview">
          <div
            class="hero-video"
            :class="{
              'hero-video--stream': isStreaming,
              'hero-video--clickable': canStream && streamStatus === 'idle',
            }"
          >
            <template v-if="canStream">
              <div v-if="isStreaming" class="hero-stream">
                <!-- Keyed on the serial so a device switch never reuses a stream -->
                <video-stream
                  :key="`${selectedRow.serial}-${streamAttempt}`"
                  :serial-number="selectedRow.serial"
                  :device-settings="activeDevices[selectedRow.serial]"
                  height="100%"
                  @connected="streamStatus = 'live'"
                  @failed="handleStreamFailed"
                />
                <span v-if="streamStatus === 'live'" class="hero-stream-live">
                  <span class="hero-stream-pulse" />
                  LIVE
                </span>
                <a-button
                  size="small"
                  class="hero-stream-stop"
                  title="Stop stream"
                  @click="stopStream"
                >
                  <template #icon><close-outlined /></template>
                </a-button>
              </div>
              <div v-else-if="streamStatus === 'error'" class="hero-video-inner">
                <div class="hero-video-label">Live stream unavailable</div>
                <div class="hero-video-sub">{{ streamError }}</div>
                <a-button
                  size="small"
                  type="primary"
                  class="mt-2"
                  @click="startStream"
                >
                  Retry
                </a-button>
              </div>
              <div
                v-else
                class="hero-video-inner"
                role="button"
                tabindex="0"
                @click="startStream"
                @keydown.enter="startStream"
              >
                <play-circle-outlined class="hero-video-icon" />
                <div class="hero-video-label">Live feed preview</div>
                <div class="hero-video-sub">
                  Click to open stream
                </div>
              </div>
            </template>
            <template v-else>
              <div class="hero-video-inner hero-video-inner--muted">
                <span class="hero-video-icon">—</span>
                <div class="hero-video-label">No live feed</div>
                <div class="hero-video-sub">Device is {{ modeLabel(selectedRow.mode) }}</div>
              </div>
            </template>
          </div>

          <div
            class="hero-metrics"
            :class="{ 'hero-metrics--no-roi': !hasValueStoryAccess }"
          >
            <div class="hero-metric">
              <div class="hero-metric-value">
                {{ selectedRow.mode === 'recording'
                  ? selectedRow.hoursRecorded || '—'
                  : formatNumber(selectedRow.cyclesToday) }}
              </div>
              <div class="hero-metric-label">
                {{ selectedRow.mode === 'recording' ? 'Recorded today' : 'Cycles today' }}
              </div>
            </div>
            <div class="hero-metric">
              <div class="hero-metric-value">
                {{
                  selectedRow.badPct != null
                    ? selectedRow.badPct.toFixed(1) + '%'
                    : '—'
                }}
              </div>
              <div class="hero-metric-label">Bad rate</div>
            </div>
            <div
              v-if="hasValueStoryAccess"
              class="hero-metric hero-metric--roi"
            >
              <div class="hero-metric-value">
                <template v-if="selectedRow.hasConfig && selectedRow.mode === 'guidance'">
                  {{ formatMoney(selectedRow.costAvoided) }}
                </template>
                <template v-else>—</template>
              </div>
              <div class="hero-metric-label">
                <template v-if="selectedRow.hasConfig && selectedRow.mode === 'guidance'">
                  Cost avoided today
                </template>
                <template v-else-if="selectedRow.mode === 'guidance'">
                  <span
                    class="hero-config-link"
                    @click="handleConfigure(selectedRow)"
                  >
                    Configure value story →
                  </span>
                </template>
                <template v-else>Not available</template>
              </div>
            </div>
          </div>

          <div class="hero-bottom-row">
            <div class="hero-chart">
              <div class="hero-chart-title">
                Cycles today · hourly
                <span class="hero-chart-tz">(device local time)</span>
              </div>
              <div
                v-if="hourlyLoading && !hourlyBuckets.length"
                class="hero-chart-empty"
              >
                Loading…
              </div>
              <template v-else-if="hourlyBars.length">
                <div class="hero-chart-bars">
                  <div
                    v-for="(bar, i) in hourlyBars"
                    :key="i"
                    class="hero-chart-bar"
                    :style="{ height: bar.height + '%' }"
                    :title="`${bar.hour}:00 · ${bar.cycles} cycles`"
                  />
                </div>
                <div class="hero-chart-axis">
                  <span>0:00</span><span>6:00</span><span>12:00</span
                  ><span>18:00</span><span>24:00</span>
                </div>
              </template>
              <div v-else class="hero-chart-empty">No cycles today</div>
            </div>

            <div class="hero-missed">
              <div class="hero-chart-title">Top missed steps today</div>
              <div v-if="selectedRow.topMissed.length" class="hero-missed-list">
                <div
                  v-for="(step, i) in selectedRow.topMissed"
                  :key="i"
                  class="hero-missed-item"
                >
                  <span class="hero-missed-rank">{{ i + 1 }}</span>
                  <div class="hero-missed-body">
                    <div
                      class="hero-missed-name"
                      :title="step.step_name"
                    >
                      {{ step.step_name }}
                    </div>
                    <div class="hero-missed-bar-wrap">
                      <div
                        class="hero-missed-bar"
                        :style="{
                          width:
                            selectedRow.topMissed[0]?.count
                              ? (step.count /
                                  selectedRow.topMissed[0].count) *
                                  100 +
                                '%'
                              : '0%',
                        }"
                      />
                    </div>
                  </div>
                  <span class="hero-missed-count">{{ step.count }}</span>
                </div>
              </div>
              <div v-else class="hero-missed-empty">No misses today</div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="hero-empty">
        <laptop-outlined class="hero-empty-icon" />
        <div>Select a device from the list</div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CloseOutlined,
  LaptopOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  StarFilled,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import TelemetryService from 'src/services/telemetry';
import { roughCostAvoided } from 'src/utils/valueStory/enrichSteps';
import { mapGetters } from 'vuex';
import { formatHoursRecorded } from './hoursRecorded';
import VideoStream from './VideoStream.vue';

const MODE_LABELS = {
  guidance: 'Guidance',
  recording: 'Recording',
  idle: 'Idle',
  offline: 'Offline',
};
const MODE_TAG_COLORS = {
  guidance: 'success',
  recording: 'purple',
  idle: 'warning',
  offline: 'default',
};
const MODE_DOT_COLORS = {
  guidance: '#52c41a',
  recording: '#722ed1',
  idle: '#faad14',
  offline: '#bfbfbf',
};

export default {
  components: {
    CloseOutlined,
    LaptopOutlined,
    LineChartOutlined,
    PlayCircleOutlined,
    StarFilled,
    VideoStream,
  },
  props: {
    devices: { type: Array, required: true, default: () => [] },
    activeDevices: { type: Object, required: true, default: () => ({}) },
    activeDevicesStatus: { type: Object, required: true, default: () => ({}) },
    idToTaskNameMapping: { type: Object, required: true, default: () => ({}) },
    valueStoryConfigMap: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      searchTerm: '',
      selectedSerial: null,
      hourlyBuckets: [],
      hourlyLoading: false,
      hourlyKey: null,
      // Inline live stream: 'idle' | 'connecting' | 'live' | 'error'
      streamStatus: 'idle',
      streamError: '',
      streamAttempt: 0,
    };
  },
  computed: {
    ...mapGetters(['hasValueStoryAccess']),
    rows() {
      return this.devices.map((d) => this.buildRow(d));
    },
    filteredRows() {
      const q = this.searchTerm.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter((r) =>
        r.deviceName.toLowerCase().includes(q) ||
        (r.taskName || '').toLowerCase().includes(q)
      );
    },
    selectedRow() {
      if (!this.selectedSerial) return null;
      return this.rows.find((r) => r.serial === this.selectedSerial) || null;
    },
    canStream() {
      const mode = this.selectedRow?.mode;
      return mode === 'guidance' || mode === 'recording';
    },
    isStreaming() {
      return (
        this.canStream &&
        (this.streamStatus === 'connecting' || this.streamStatus === 'live')
      );
    },
    hourlyBars() {
      const buckets = this.hourlyBuckets;
      if (!Array.isArray(buckets) || !buckets.length) return [];
      const max = buckets.reduce(
        (m, b) => Math.max(m, Number(b.cycles) || 0),
        0
      );
      if (max <= 0) return [];
      return buckets.map((b) => ({
        hour: b.hour,
        cycles: Number(b.cycles) || 0,
        height: ((Number(b.cycles) || 0) / max) * 100,
      }));
    },
  },
  watch: {
    rows: {
      handler(list) {
        if (this.selectedSerial) return;
        const starredGuidance = list.find(
          (r) => r.starred && r.mode === 'guidance'
        );
        const guidance = list.find((r) => r.mode === 'guidance');
        const active = list.find((r) => r.isActive);
        this.selectedSerial =
          starredGuidance?.serial ||
          guidance?.serial ||
          active?.serial ||
          list[0]?.serial ||
          null;
      },
      immediate: true,
    },
    // The stream belongs to one device in a streamable mode; drop it (and any
    // error left from it) when either changes.
    selectedSerial() {
      this.stopStream();
    },
    canStream(can) {
      if (!can) this.stopStream();
    },
    selectedRow: {
      handler(row) {
        if (!row?.serial || !row.taskId || !row.isActive) {
          this.hourlyBuckets = [];
          this.hourlyKey = null;
          return;
        }
        this.fetchHourly(row.serial, row.taskId);
      },
      immediate: true,
    },
  },
  methods: {
    buildRow(device) {
      const settings = this.activeDevices[device.Serial_number];
      const active = Boolean(settings?.active);
      const guidance = Boolean(settings?.isInferenceRunning);
      const recording = Boolean(settings?.record_shift);
      let mode = 'offline';
      if (active) mode = recording ? 'recording' : guidance ? 'guidance' : 'idle';
      const taskId = settings?.Task;
      const stats = this.activeDevicesStatus[`${device.Serial_number}${taskId}`] || {};
      const cycles = Number(stats.number_of_cycles);
      const bad = Number(stats.bad_cycles);
      const cfg = this.valueStoryConfigMap?.[taskId];
      const hasConfig = !!(
        cfg &&
        ((cfg.steps && Object.keys(cfg.steps).length) ||
          (cfg.economics && Object.keys(cfg.economics).length))
      );
      const costAvoided = hasConfig && mode === 'guidance' && bad
        ? roughCostAvoided(cfg, bad)
        : 0;
      return {
        serial: device.Serial_number,
        deviceName: device.display_name || device.Serial_number,
        starred: !!device.starred,
        isActive: active,
        mode,
        taskId,
        taskName: this.idToTaskNameMapping[taskId] || '',
        cyclesToday: Number.isFinite(cycles) ? cycles : null,
        hoursRecorded: formatHoursRecorded(stats.hours_recorded),
        badPct:
          Number.isFinite(cycles) && cycles > 0 && Number.isFinite(bad)
            ? (bad / cycles) * 100
            : null,
        hasConfig,
        costAvoided,
        topMissed: Array.isArray(stats.top_missed_steps)
          ? stats.top_missed_steps.slice(0, 3)
          : [],
      };
    },
    async fetchHourly(serial, taskId) {
      const date = dayjs().format('YYYY-MM-DD');
      const key = `${serial}|${taskId}|${date}`;
      if (this.hourlyKey === key) return;
      this.hourlyKey = key;
      this.hourlyLoading = true;
      const [error, data] = await TelemetryService.fetchHourlyCycles({
        device: serial,
        task: taskId,
        date,
      });
      this.hourlyLoading = false;
      if (error || key !== this.hourlyKey) return;
      this.hourlyBuckets = Array.isArray(data?.buckets) ? data.buckets : [];
    },
    // Click-to-start only: every stream makes the device encode and send video.
    startStream() {
      if (!this.canStream) return;
      this.streamError = '';
      this.streamAttempt += 1;
      this.streamStatus = 'connecting';
    },
    // Unmounts the player, which closes its connection.
    stopStream() {
      this.streamStatus = 'idle';
      this.streamError = '';
    },
    handleStreamFailed(message) {
      this.streamError = message || 'Could not start the live stream';
      this.streamStatus = 'error';
    },
    modeLabel(m) {
      return MODE_LABELS[m];
    },
    modeTagColor(m) {
      return MODE_TAG_COLORS[m];
    },
    modeDotColor(m) {
      return MODE_DOT_COLORS[m];
    },
    formatNumber(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString();
    },
    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '$0';
      if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
      return `$${Math.round(n).toLocaleString()}`;
    },
    handleOpenAnalytics(row) {
      if (!row.isActive || !row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Operation Level Analytics',
        query: {
          taskId: row.taskId,
          deviceId: JSON.stringify([row.serial]),
        },
      });
      window.open(href, '_blank');
    },
    handleOpenTrace(row) {
      if (!row.isActive || !row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Trace',
        query: {
          taskId: row.taskId,
          deviceId: JSON.stringify([row.serial]),
        },
      });
      window.open(href, '_blank');
    },
    handleConfigure(row) {
      if (!row.taskId) return;
      const { href } = this.$router.resolve({
        name: 'Define Operation',
        query: { openValueStory: row.taskId },
      });
      window.open(href, '_blank');
    },
  },
};
</script>

<style scoped>
.hero-split {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 12px;
  flex: 1;
  min-height: 0;
  height: 100%;
}
.rail {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
  min-height: 0;
}
.rail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rail-title {
  font-weight: 600;
  color: #1a1a1a;
}
.rail-search {
  width: 100%;
}
.rail-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
}
.rail-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.rail-item:hover {
  background: #fafafa;
}
.rail-item--selected {
  background: #e6f7ff;
}
.rail-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.rail-item-body {
  min-width: 0;
  flex: 1;
}
.rail-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}
.rail-item-sub {
  font-size: 11px;
  color: #8c8c8c;
}
.rail-star {
  color: #faad14;
  font-size: 11px;
}

.hero {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
}
.hero-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  color: #8c8c8c;
}
.hero-empty-icon {
  font-size: 40px;
}
.hero-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.hero-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hero-star {
  color: #faad14;
  font-size: 14px;
}
.hero-subtitle {
  color: #1890ff;
  font-weight: 500;
  margin-top: 2px;
}
.hero-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
}
.hero-video {
  background: linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%);
  border: 1px dashed #adc6ff;
  border-radius: 10px;
  flex: 1;
  min-height: 120px;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-video--clickable {
  cursor: pointer;
}
.hero-video--clickable:hover {
  border-color: #597ef7;
}
.hero-video--stream {
  background: #0f172a;
  border: 1px solid #0f172a;
  overflow: hidden;
  /* Fixed rather than flexible so the video has a definite box to fill */
  flex: 0 0 auto;
  height: clamp(200px, 40vh, 420px);
  max-height: none;
}
.hero-stream {
  position: relative;
  width: 100%;
  height: 100%;
}
.hero-stream :deep(video) {
  display: block;
  width: 100%;
  object-fit: contain;
  background: #000;
}
.hero-stream :deep(.spinner-overlay) {
  color: #ffffff;
}
.hero-stream-live {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff1f0;
  color: #cf1322;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.hero-stream-pulse {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #f5222d;
  animation: hero-stream-pulse 1.6s ease-in-out infinite;
}
@keyframes hero-stream-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 34, 45, 0.55);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 34, 45, 0);
  }
}
.hero-stream-stop {
  position: absolute;
  top: 8px;
  right: 8px;
}
.hero-video-inner {
  text-align: center;
  color: #1d39c4;
}
.hero-video-inner[role='button'] {
  cursor: pointer;
}
.hero-video-inner--muted {
  color: #8c8c8c;
}
.hero-video-icon {
  font-size: 32px;
  display: block;
  margin-bottom: 6px;
}
.hero-video-label {
  font-weight: 600;
  font-size: 14px;
}
.hero-video-sub {
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
}
.hero-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.hero-metrics--no-roi {
  grid-template-columns: repeat(2, 1fr);
}
.hero-metric {
  background: #fafafa;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
}
.hero-metric-value {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
  margin-bottom: 3px;
}
.hero-metric-label {
  font-size: 10px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.hero-metric--roi {
  background: #f6ffed;
}
.hero-metric--roi .hero-metric-value {
  color: #237804;
}
.hero-config-link {
  color: #1890ff;
  cursor: pointer;
  text-transform: none;
  letter-spacing: normal;
}
.hero-config-link:hover {
  text-decoration: underline;
}
.hero-bottom-row {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 12px;
}
@media (max-width: 900px) {
  .hero-bottom-row {
    grid-template-columns: 1fr;
  }
}
.hero-chart {
  background: #fafafa;
  border-radius: 10px;
  padding: 10px 12px;
}
.hero-missed {
  background: #fafafa;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
}
.hero-missed-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.hero-missed-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hero-missed-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: #ffbb96;
  color: #7c2d00;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
}
.hero-missed-body {
  flex: 1;
  min-width: 0;
}
.hero-missed-name {
  font-size: 12px;
  color: #595959;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}
.hero-missed-bar-wrap {
  height: 6px;
  background: #f5f5f5;
  border-radius: 999px;
  overflow: hidden;
}
.hero-missed-bar {
  height: 100%;
  background: linear-gradient(90deg, #ffbb96, #fa8c16);
  border-radius: 999px;
}
.hero-missed-count {
  font-weight: 700;
  color: #ad4e00;
  font-size: 13px;
  min-width: 28px;
  text-align: right;
}
.hero-missed-empty {
  color: #bfbfbf;
  font-size: 13px;
  padding: 12px 0;
  text-align: center;
}
.hero-chart-title {
  font-size: 10px;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.hero-chart-tz {
  text-transform: none;
  letter-spacing: normal;
  margin-left: 4px;
  color: #bfbfbf;
  font-weight: 400;
}
.hero-chart-empty {
  color: #bfbfbf;
  font-size: 12px;
  text-align: center;
  padding: 20px 0;
}
.hero-chart-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 70px;
}
.hero-chart-bar {
  flex: 1;
  background: linear-gradient(180deg, #91d5ff, #1890ff);
  border-radius: 3px 3px 0 0;
  transition: opacity 0.2s ease;
}
.hero-chart-bar:hover {
  opacity: 0.8;
}
.hero-chart-axis {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #bfbfbf;
  margin-top: 6px;
}
.dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  margin-right: 6px;
  vertical-align: middle;
}
@media (max-width: 1200px) {
  .hero-split {
    grid-template-columns: 220px 1fr;
  }
  .hero-metric-value {
    font-size: 18px;
  }
}
@media (max-width: 900px) {
  .hero-split {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(180px, 220px) 1fr;
  }
  .rail {
    min-height: 0;
  }
}
@media (max-width: 480px) {
  .hero-video {
    min-height: 120px;
    max-height: 140px;
  }
  .hero-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
